import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { DvmModule, MapViewerEvent, MapViewerService } from '@3ddv/ngx-dvm-internal';
import { ViewersService } from '../../../services/viewers.service';
import { Subscription, take } from 'rxjs';
import { MapViewerEventObject, MapViewerNode } from '@3ddv/dvm-internal';
import { CloseFullscreenButtonComponent } from '../overlay-btns/close-fullscreen-button/close-fullscreen-button.component';
import { SiteConfiguration } from '../../../configuration/configuration';
import { PopoverService } from '../../../services/popover.service';
import { VrButtonComponent } from '../overlay-btns/vr-button/vr-button.component';
import { CommonModule } from '@angular/common';
import { MinimapComponent } from '../minimap/minimap.component';

@Component({
  selector: 'app-viewers',
  imports: [CommonModule, DvmModule, CloseFullscreenButtonComponent, VrButtonComponent, MinimapComponent],
  templateUrl: './viewers.component.html',
  styleUrl: './viewers.component.css'
})
export class ViewersComponent implements AfterViewInit, OnDestroy {
  @Input() isVrView?: boolean
  private subscriptions: Subscription[] = [];
  public is3dView: boolean = false;
  // public status: { map: any; view: { show: boolean, error: boolean }; minimap: { show: boolean, error: boolean } } = {
  //   map: {},
  //   view: { show: false, error: false },
  //   minimap: { show: true, error: false },
  // };

  constructor(
    public readonly viewersService: ViewersService,
    private readonly popoverService: PopoverService
  ) { }

  //#region  LIFECYCLE HOOKS
  ngAfterViewInit() {
    
    if(!this.isVrView){
      // Initialize the viewers
      this.initViewersComponent();
    } else {
      // Initialize only 3d
      this.viewersService.initVr();
    }

    const is3d = this.viewersService.is3dView.subscribe((is3dView: boolean) => {
      this.is3dView = is3dView;
      // this.status.view.show = is3dView;
      // this.status.view.error = !is3dView;
    });
    this.subscriptions.push(is3d);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }
  //#endregion

  //#region LIFECYCLE METHODS
  private initViewersComponent() {
    const viewer: MapViewerService = this.viewersService.viewer;
    const minimap: MapViewerService = this.viewersService.minimap;

    // Init viewers
    this.viewersService.initViewers();

    // Start viewer callbacks
    const waitInitViewer = viewer.waitInitialize().subscribe((): void => this.initViewerCallbakcs());
    const waitInitMinimap = minimap.waitInitialize().subscribe((): void => this.initMinimapCallbakcs());
    this.subscriptions.push(waitInitViewer, waitInitMinimap);

    // // Viewer
    // const viewer = this.viewersService.viewer.initialize({ plugins: ['navigable_minimap'] }).subscribe((): void => this.initViewerCallbakcs());
    // // Viewer 3D
    // const viewer3d = this.viewersService.viewer3d.initialize().subscribe(() => { });
    // // Minimap
    // const minimap = this.viewersService.viewer.waitInitialize().subscribe(() => { });

    // // Add the subscriptions to the array
    // this.subscriptions.push(viewer, viewer3d, minimap);
  }

  private initViewerCallbakcs() {
    const viewer: MapViewerService = this.viewersService.viewer;

    // LOAD event
    const success = viewer.getObservable('load_success').subscribe(() => this.handleLoadSuccess());

    // CLICK event
    const click = viewer.getObservable('click').subscribe((event: MapViewerEvent<MapViewerEventObject>) => this.handleClick(event['event']));

    // ENTER event
    const enter = viewer.getObservable('enter').subscribe((event: MapViewerEvent<MapViewerEventObject>) => this.handleEnter(event['event']));

    // LEAVE event
    const leave = viewer.getObservable('leave').subscribe((event: MapViewerEvent<MapViewerEventObject>) => this.handleLeave(event['event']));

    // Push the subscriptions to the array
    this.subscriptions.push(success, click, enter, leave);
  }

  private initMinimapCallbakcs() {
    const minimap: MapViewerService = this.viewersService.minimap;

    // LOAD event
    const success = minimap.getObservable('load_success').subscribe(() => this.handleMinimapLoadSuccess());

    // Push the subscriptions to the array
    this.subscriptions.push(success);
  }
  //#endregion

  //#region HANDLE METHODS
  private handleLoadSuccess(): void {
    // Set custom map styles
    this.setMapStyles();
  }
  
  private handleMinimapLoadSuccess(): void {
    // Set custom map styles
    this.setMinimapStyles();
  }

  private handleClick(event: MapViewerEventObject): void {
    const node: MapViewerNode = event.nodes[0];

    if (!node || (!node.state || node.state === 'unavailable')) return;

    switch (node.type) {
      case 'section':
        this.onSectionClick(node);
        break;
      case 'seat':
        this.onSeatClick(node);
        break;
    }
  }

  private handleEnter(event: MapViewerEventObject): void {
    // console.log('Enter event: ', event);
    const node: MapViewerNode = event.nodes[0];
    if (!node || (!node.state || node.state === 'unavailable')) return;
    // Show the popover for the node
    this.popoverService.showPopover(node);
  }

  private handleLeave(event: MapViewerEventObject): void {
    // console.log('Leave event: ', event);
    const node: MapViewerNode = event.nodes[0];
    // Hide the popover for the node
    this.popoverService.hidePopover(node);
  }


  // CLICK METHODS
  private onSectionClick(node: MapViewerNode): void {
    // Enter Section
    this.viewersService.viewer.unselectAll();
    this.viewersService.viewer.select(node.id);
    const navigate = this.viewersService.navigateToSection(node.id).pipe(take(1)).subscribe();
    this.subscriptions.push(navigate);
    // Hide the popover for the node
    this.popoverService.hidePopover(node);
  }

  private onSeatClick(node: MapViewerNode): void {
    // Enter Seat
    this.viewersService.viewer.unselectAll();
    this.viewersService.viewer.select(node.id);
    const loadView = this.viewersService.load3dView(node.id).pipe(take(1)).subscribe();
    this.subscriptions.push(loadView);
    // Hide the popover for the node
    this.popoverService.hidePopover(node);
  }
  //#endregion

  /**
   * Set custom map styles to the viewer.
   * This method is called after the map is loaded successfully.
   */
  private setMapStyles() {
    let styles: any = this.viewersService.viewer.getStyles();
    // console.log('Map Styles: ', styles);

    const siteStyles = SiteConfiguration.map.styles;

    // Section Styles changes
    if (styles && styles.length > 0 && styles[0].section) {
      // Available Normal
      Object.assign(styles[0].section.available.normal.none, siteStyles.section.available.normal.none);
      // Available Hover
      Object.assign(styles[0].section.available.hover.none, siteStyles.section.available.hover.none);
      // Selected Normal
      Object.assign(styles[0].section.selected.normal.none, siteStyles.section.selected.normal.none);
      // Selected Hover
      Object.assign(styles[0].section.selected.hover.none, siteStyles.section.selected.hover.none);
      // Unavailable Normal
      Object.assign(styles[0].section.unavailable.normal.none, siteStyles.section.unavailable.normal.none);
      // Disabled Normal
      Object.assign(styles[0].section.disabled.normal.none, siteStyles.section.disabled.normal.none);
    }

    // Set styles to viewer
    this.viewersService.viewer.setStyles(styles);
  }

  /**
   * Set custom map styles to the viewer.
   * This method is called after the map is loaded successfully.
   */
  private setMinimapStyles() {
    let styles: any = this.viewersService.minimap.getStyles();
    // console.log('Minimap Styles: ', styles);

    // Section Styles changes
    if (styles && styles.length > 0 && styles[0].section) {
      // Available Normal
      // styles[0].section.available.normal.none.fillStyle = '#ffffff';
      // styles[0].section.available.normal.none.strokeStyle = '#ffffff';
      // Selected Normal
      styles[0].section.selected.normal.none.fillStyle = '#ffffff';
      styles[0].section.selected.normal.none.strokeStyle = '#ffffff';
    }

    // Set styles to minimap
    this.viewersService.minimap.setStyles(styles);
  }

}
